export type GoogleMapsParseStep = {
  id: string;
  label: string;
  pattern: string;
  tried: boolean;
  matched: boolean;
  matchSnippet?: string;
  lat?: number;
  long?: number;
  note?: string;
};

export type GoogleMapsHighlight = {
  start: number;
  end: number;
  kind: "lat" | "long" | "pair";
};

export type GoogleMapsParseResult = {
  lat: number | null;
  long: number | null;
  source: GoogleMapsParseStep["id"] | null;
  steps: GoogleMapsParseStep[];
  highlights: GoogleMapsHighlight[];
};

const PATTERNS: Array<{
  id: GoogleMapsParseStep["id"];
  label: string;
  pattern: string;
  regex: RegExp;
  note: string;
  pick: (m: RegExpMatchArray) => { lat: number; long: number };
}> = [
  {
    id: "3d4d",
    label: "Place pin (!3d…!4d…)",
    pattern: "!3d{lat}!4d{long}",
    regex: /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    note: "Most accurate — exact pinned location",
    pick: (m) => ({ lat: Number(m[1]), long: Number(m[2]) }),
  },
  {
    id: "at",
    label: "Map center (@lat,long)",
    pattern: "@{lat},{long}",
    regex: /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    note: "Viewport center — may differ slightly from the pin",
    pick: (m) => ({ lat: Number(m[1]), long: Number(m[2]) }),
  },
  {
    id: "q",
    label: "Query param (?q=lat,long)",
    pattern: "?q={lat},{long}",
    regex: /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    note: "Search/query coordinates",
    pick: (m) => ({ lat: Number(m[1]), long: Number(m[2]) }),
  },
  {
    id: "ll",
    label: "Query param (?ll=lat,long)",
    pattern: "?ll={lat},{long}",
    regex: /[?&]ll=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    note: "Legacy ll= parameter",
    pick: (m) => ({ lat: Number(m[1]), long: Number(m[2]) }),
  },
];

export function parseGoogleMapsUrl(url: string): GoogleMapsParseResult {
  const trimmed = url.trim();
  const steps: GoogleMapsParseStep[] = [];
  const highlights: GoogleMapsHighlight[] = [];

  if (!trimmed) {
    return { lat: null, long: null, source: null, steps, highlights };
  }

  for (const p of PATTERNS) {
    const match = trimmed.match(p.regex);
    const step: GoogleMapsParseStep = {
      id: p.id,
      label: p.label,
      pattern: p.pattern,
      tried: true,
      matched: !!match,
      note: p.note,
    };

    if (match) {
      const { lat, long } = p.pick(match);
      step.matchSnippet = match[0];
      step.lat = lat;
      step.long = long;

      const start = match.index ?? 0;
      const end = start + match[0].length;
      if (p.id === "3d4d") {
        const latMatch = match[0].match(/!3d(-?\d+(?:\.\d+)?)/)!;
        const longMatch = match[0].match(/!4d(-?\d+(?:\.\d+)?)/)!;
        highlights.push({
          start: start + latMatch.index!,
          end: start + latMatch.index! + latMatch[0].length,
          kind: "lat",
        });
        highlights.push({
          start: start + longMatch.index!,
          end: start + longMatch.index! + longMatch[0].length,
          kind: "long",
        });
      } else {
        highlights.push({ start, end, kind: "pair" });
      }

      steps.push(step);
      return { lat, long, source: p.id, steps, highlights };
    }

    steps.push(step);
  }

  return { lat: null, long: null, source: null, steps, highlights };
}
